import {
  Music2, FlaskConical, Mic, Lock, ArrowLeft, ArrowUpRight,
} from "lucide-react";
import { CoinBalanceChip } from "@/components/mist/shared";
import { VoiceLab, RecordingCrib, VoiceVault } from "@/components/mist/VoiceStudios";
import MistMusicStudio from "@/components/mist/MistMusicStudio";
import { useStudioParam } from "@/hooks/useStudioUrl";

/* ============================ STUDIO LANDING ============================= */
const TILES = [
  { k: "studio", title: "Mist Music Studio", sub: "Setup → lyrics → master track", Icon: Music2, color: "#9EFFB8", live: true },
  { k: "lab", title: "AI Voice Lab", sub: "Synthesize custom voices", Icon: FlaskConical, color: "#A4DCFA", live: true },
  { k: "crib", title: "Recording Crib", sub: "10s mic capture booth", Icon: Mic, color: "#FFC9A3", live: true },
  { k: "vault", title: "Voice Vault Archive", sub: "Saved voice takes", Icon: Lock, color: "#E8D3FF", live: true },
];

function PlaceholderTile({ tile, onBack }) {
  return (
    <div className="h-full w-full flex flex-col" data-testid={`music-placeholder-${tile.k}`}>
      <div className="shrink-0 px-4 pt-3 pb-1">
        <button onClick={onBack} data-testid="music-tile-back" className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/60">
          <ArrowLeft size={13} /> Music Studios
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4">
        <div className="border-4 border-[#2a2a2a] rounded-3xl bg-[#141414] p-6 shadow-[4px_4px_0px_#1A1A1A] flex flex-col items-center text-center gap-3">
          <span className="w-16 h-16 rounded-2xl border-2 border-[#2a2a2a] grid place-items-center" style={{ backgroundColor: tile.color }}>
            <tile.Icon size={30} strokeWidth={2.2} className="text-white" />
          </span>
          <p className="font-serif text-2xl text-white">{tile.title}</p>
          <p className="font-mono text-[11px] text-white/55 leading-relaxed">{tile.sub}. This studio is being wired up — check back soon.</p>
          <span className="font-mono text-[8px] font-black uppercase tracking-[0.2em] bg-charcoal text-gold px-3 py-1 rounded-full">Coming Soon</span>
        </div>
      </div>
    </div>
  );
}

export default function MistMusic() {
  const [tile, setTile] = useStudioParam("view", "");
  const back = () => setTile("");

  if (tile === "studio") return <MistMusicStudio onBack={back} />;
  if (tile === "lab") return <VoiceLab onBack={back} />;
  if (tile === "crib") return <RecordingCrib onBack={back} />;
  if (tile === "vault") return <VoiceVault onBack={back} />;
  const found = TILES.find((t) => t.k === tile);
  if (found) return <PlaceholderTile tile={found} onBack={back} />;

  return (
    <div className="h-full w-full flex flex-col" data-testid="mist-music-hub">
      <div className="shrink-0 flex items-center justify-between px-4 pt-3 pb-1">
        <h3 className="font-serif text-xl text-white">Mist Music</h3>
        <CoinBalanceChip />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4">
        <p className="font-mono text-[10px] text-white/60 leading-relaxed border-2 border-dashed border-[#2a2a2a]/30 rounded-xl p-2.5 mb-4">
          Pick a studio. <b>Mist Music Studio</b> writes lyrics &amp; produces master tracks end-to-end. <b>Voice Lab</b>, <b>Crib</b> &amp; <b>Vault</b> handle custom voices.
        </p>
        <div className="grid grid-cols-1 gap-3" data-testid="music-tile-grid">
          {TILES.map((t) => (
            <button key={t.k} onClick={() => setTile(t.k)} data-testid={`music-tile-${t.k}`}
              className="flex items-center gap-3 rounded-2xl border-2 border-[#2a2a2a] p-4 text-left shadow-[4px_4px_0px_#1A1A1A] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform duration-150"
              style={{ backgroundColor: t.color }}>
              <span className="w-12 h-12 shrink-0 rounded-xl border-2 border-black/15 bg-white/70 grid place-items-center">
                <t.Icon size={24} strokeWidth={2.2} className="text-black" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-xl text-black leading-tight">{t.title}</p>
                <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-black/70 mt-0.5">{t.sub}</p>
              </div>
              {t.live
                ? <span className="shrink-0 font-mono text-[7px] font-black uppercase tracking-widest bg-charcoal text-gold px-2 py-1 rounded-full">Live</span>
                : <ArrowUpRight size={16} className="shrink-0 text-black/50" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
